import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Bell, Check, CheckCheck, ExternalLink, ArrowRight } from "lucide-react";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { useLocation, useSearch } from "wouter";

interface ClientNotification {
  id: string;
  title: string;
  body: string;
  link: string | null;
  createdAt: string;
  read: boolean;
  readAt: string | null;
}

export default function ClientNotifications() {
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const [selectedNotif, setSelectedNotif] = useState<ClientNotification | null>(null);
  const autoOpenedRef = useRef(false);

  const { data: notifications, isLoading } = useQuery<ClientNotification[]>({
    queryKey: ["/api/client/notifications"],
  });

  const markReadMutation = useMutation({
    mutationFn: (id: string) => apiRequest("POST", `/api/client/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/client/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/client/notifications/unread-count"] });
    },
  });

  useEffect(() => {
    if (!notifications || autoOpenedRef.current) return;
    const params = new URLSearchParams(searchString);
    const notifId = params.get("id");
    if (notifId) {
      const notif = notifications.find(n => n.id === notifId);
      if (notif) {
        setSelectedNotif(notif);
        if (!notif.read) markReadMutation.mutate(notif.id);
        autoOpenedRef.current = true;
        setLocation("/notifications", { replace: true });
      }
    }
  }, [notifications, searchString, setLocation]);

  const handleNotifClick = (notif: ClientNotification) => {
    if (!notif.read) {
      markReadMutation.mutate(notif.id);
    }
    setSelectedNotif(notif);
  };

  const handleLinkClick = (link: string) => {
    if (link.startsWith("http")) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      setSelectedNotif(null);
      setLocation(link);
    }
  };

  const unreadCount = notifications?.filter(n => !n.read).length || 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold" data-testid="text-notifications-title">Notifikationer</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {unreadCount > 0 ? `${unreadCount} olästa` : "Alla lästa"}
          </p>
        </div>
      </div>

      {notifications && notifications.length > 0 ? (
        <div className="space-y-3">
          {notifications.map((notif) => (
            <Card
              key={notif.id}
              className={`hover-elevate cursor-pointer ${!notif.read ? "border-primary/30" : ""}`}
              onClick={() => handleNotifClick(notif)}
              data-testid={`notification-card-${notif.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className={`h-8 w-8 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${!notif.read ? "bg-primary/10" : "bg-muted"}`}>
                    <Bell className={`h-4 w-4 ${!notif.read ? "text-primary" : "text-muted-foreground"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium ${notif.read ? "text-muted-foreground" : ""}`} data-testid={`text-notif-title-${notif.id}`}>
                          {notif.title}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2" data-testid={`text-notif-body-${notif.id}`}>
                          {notif.body}
                        </p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(notif.createdAt), "d MMMM yyyy, HH:mm", { locale: sv })}
                          </p>
                          {notif.link && (
                            <Badge variant="secondary" className="text-xs">
                              {notif.link.startsWith("http") ? <ExternalLink className="h-3 w-3 mr-1" /> : <ArrowRight className="h-3 w-3 mr-1" />}
                              Länk
                            </Badge>
                          )}
                          {notif.read && (
                            <Badge variant="secondary" className="text-xs">
                              <CheckCheck className="h-3 w-3 mr-1" />
                              Läst
                            </Badge>
                          )}
                        </div>
                      </div>
                      {!notif.read && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            markReadMutation.mutate(notif.id);
                          }}
                          data-testid={`button-mark-read-${notif.id}`}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Inga notifikationer att visa</p>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!selectedNotif} onOpenChange={() => setSelectedNotif(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedNotif?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm whitespace-pre-wrap" data-testid="text-notif-full-body">
              {selectedNotif?.body}
            </p>
            {selectedNotif?.link && (
              <Button
                variant="outline"
                className="w-full"
                onClick={() => handleLinkClick(selectedNotif.link!)}
                data-testid="button-notif-open-link"
              >
                {selectedNotif.link.startsWith("http") ? (
                  <>
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Öppna länk
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Gå till sidan
                  </>
                )}
              </Button>
            )}
            <p className="text-xs text-muted-foreground">
              {selectedNotif?.createdAt && format(new Date(selectedNotif.createdAt), "d MMMM yyyy, HH:mm", { locale: sv })}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
